import { useState, useEffect } from 'react';
import { X, AlertTriangle, Trash2, Loader } from 'lucide-react';

interface Template {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
}

interface DeleteTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

export default function DeleteTemplateModal({
  isOpen,
  onClose,
  template,
  onConfirm,
  isDeleting,
}: DeleteTemplateModalProps) {
  const [confirmName, setConfirmName] = useState('');

  // Reset confirmation input when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmName('');
    }
  }, [isOpen]);

  if (!isOpen || !template) return null;

  const isConfirmValid = confirmName === template.name;

  const handleConfirm = async () => {
    if (!isConfirmValid || isDeleting) return;
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={!isDeleting ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-bg-1 border border-border-1 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-1 bg-danger/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger/20 rounded-lg">
              <AlertTriangle className="text-danger" size={24} />
            </div>
            <h2 className="text-xl font-bold text-text-1">Deletar Template</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="p-2 hover:bg-bg-3 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="text-text-3" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Template Name */}
          <div className="bg-bg-2 border border-border-1 rounded-lg p-4">
            <p className="text-sm text-text-3 mb-1">Template a ser deletado:</p>
            <p className="text-lg font-semibold text-text-1">{template.name}</p>
            <p className="text-sm text-text-3 mt-1">
              {template.language} • {template.category}
            </p>
          </div>

          {/* Warnings */}
          <div className="bg-danger/10 border border-danger rounded-lg p-4 space-y-3">
            <p className="text-sm font-medium text-danger flex items-center gap-2">
              <AlertTriangle size={16} />
              ATENÇÃO: Esta ação é IRREVERSÍVEL!
            </p>
            <ul className="text-sm text-danger space-y-2 ml-6 list-disc">
              <li>
                <strong>TODAS</strong> as variações de idioma deste template serão deletadas
              </li>
              <li>
                O nome do template <strong>não poderá ser reutilizado por 30 dias</strong>
              </li>
              <li>
                Mensagens já enviadas mas não entregues tentarão entrega por 30 dias
              </li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm text-text-2 mb-2">
              Para confirmar, digite o nome do template: <strong className="text-text-1">{template.name}</strong>
            </label>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={template.name}
              disabled={isDeleting}
              className="w-full px-4 py-2 bg-bg-2 border border-border-1 rounded-lg text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-danger focus:border-transparent disabled:opacity-50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-border-1 bg-bg-2">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 bg-bg-3 hover:bg-bg-3/80 text-text-1 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmValid || isDeleting}
            className="flex items-center gap-2 px-4 py-2 bg-danger hover:bg-danger/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <Loader className="animate-spin" size={18} />
                Deletando...
              </>
            ) : (
              <>
                <Trash2 size={18} />
                Deletar Template
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
